import { useLanguageAwareRouting } from '@gofranz/common-components';
import {
  Anchor,
  Badge,
  Box,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories, getIntegrationsByCategory, type IntegrationMeta } from './data';

interface IntegrationCardProps {
  integration: IntegrationMeta;
}

function IntegrationCard({ integration }: IntegrationCardProps) {
  const { t } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const Icon = integration.icon;

  return (
    <Card
      withBorder
      radius="md"
      p="lg"
      h="100%"
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <Group justify="space-between" align="flex-start" mb="md">
        <ThemeIcon size={48} radius="md" variant="light" color={integration.color}>
          <Icon size={24} />
        </ThemeIcon>
        {integration.badgeI18nKey && (
          <Badge variant="light" color="gray" radius="sm">
            {t(integration.badgeI18nKey)}
          </Badge>
        )}
      </Group>
      <Title order={3} size="h4" mb="xs">
        {t(integration.titleKey)}
      </Title>
      <Text size="sm" c="dimmed" style={{ flexGrow: 1 }} mb="md">
        {t(integration.subtitleKey)}
      </Text>
      <Anchor
        component={Link}
        to={createLanguageURL(`/integrations/${integration.slug}`)}
        size="sm"
        fw={500}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        {t('integrations.cards.learnMore')}
        <IconArrowRight size={14} />
      </Anchor>
    </Card>
  );
}

export function IntegrationsCategoryList() {
  const { t } = useTranslation();

  return (
    <Stack gap="xxl">
      {categories.map((category) => {
        const items = getIntegrationsByCategory(category.id);
        if (items.length === 0) return null;
        const CategoryIcon = category.icon;

        return (
          <Box key={category.id}>
            <Group
              gap="md"
              mb="lg"
              pb="md"
              style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}
            >
              <ThemeIcon size={40} radius="xl" variant="light">
                <CategoryIcon size={20} />
              </ThemeIcon>
              <Title order={2} fw={600}>
                {t(category.i18nKey)}
              </Title>
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
              {items.map((integration) => (
                <IntegrationCard key={integration.slug} integration={integration} />
              ))}
            </SimpleGrid>
          </Box>
        );
      })}
    </Stack>
  );
}
